import { useEffect, useRef, useState } from "react";

import { cn } from "~/lib/utils";

import type { ImgHTMLAttributes } from "react";

type ResilientImageProps = Omit<ImgHTMLAttributes<HTMLImageElement>, "alt" | "src"> & {
  src?: string | null;
  alt: string;
  fallbackSrc: string;
  retryCount?: number;
};

function retryUrl(source: string, attempt: number) {
  const separator = source.includes("?") ? "&" : "?";
  return `${source}${separator}image_retry=${attempt}-${Date.now()}`;
}

/**
 * Imagen que se recupera de fallos transitorios.
 *
 * Las portadas de staging pasan por `/api/file/proxy`. Si ese request falla una vez, un `onError`
 * tradicional reemplaza para siempre la portada por el placeholder, aunque el endpoint vuelva a
 * responder un segundo después. Este componente reintenta con cache-busting y sólo usa el fallback
 * cuando agotó los intentos. Mientras reintenta mantiene la imagen oculta para no mostrar el ícono
 * roto nativo del navegador.
 */
export function ResilientImage({
  src,
  fallbackSrc,
  retryCount = 3,
  alt,
  className,
  onError,
  onLoad,
  ...props
}: ResilientImageProps) {
  const requestedSource = src || fallbackSrc;
  const [currentSource, setCurrentSource] = useState(requestedSource);
  const [ready, setReady] = useState(false);
  const attemptsRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    attemptsRef.current = 0;
    setReady(false);
    setCurrentSource(requestedSource);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [requestedSource]);

  /**
   * Imagen servida desde la CACHÉ del navegador: el evento `load` puede dispararse antes
   * de que React monte el `onLoad`, y entonces `ready` se quedaba en false para siempre.
   * Como el componente se oculta hasta estar listo, la imagen terminaba cargada (complete
   * y naturalWidth correctos) pero `invisible`, dejando ver el fondo del contenedor.
   *
   * Pasaba SIEMPRE al volver a una pantalla ya visitada — atrás del navegador o link
   * interno, desktop y mobile — porque ahí las portadas ya están en caché. Refrescar lo
   * "arreglaba" porque forzaba una carga real que sí dispara el evento.
   *
   * Preguntar por `complete` después de pintar cubre el caso sin cambiar el resto.
   */
  useEffect(() => {
    const el = imgRef.current;
    if (el?.complete && el.naturalWidth > 0) setReady(true);
  }, [currentSource]);

  return (
    <img
      {...props}
      ref={imgRef}
      alt={alt}
      src={currentSource}
      className={cn("transition-opacity duration-200", ready ? "visible" : "invisible", className)}
      onLoad={(event) => {
        setReady(true);
        onLoad?.(event);
      }}
      onError={(event) => {
        onError?.(event);
        setReady(false);

        if (requestedSource === fallbackSrc || currentSource === fallbackSrc) {
          return;
        }

        if (attemptsRef.current >= retryCount) {
          setCurrentSource(fallbackSrc);
          return;
        }

        attemptsRef.current += 1;
        const attempt = attemptsRef.current;
        timerRef.current = setTimeout(
          () => setCurrentSource(retryUrl(requestedSource, attempt)),
          700 * attempt,
        );
      }}
    />
  );
}
